// ajay:code 30-07-26
import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import { productAPI, pdirAPI } from "../../services/api";

export default function PdirMaster() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [params, setParams] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({ parameter: "", instrument: "" });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    productAPI.list().then((r) => {
      const active = (r.data || []).filter((p) => p.status === "Active");
      setProducts(active);
      if (active.length > 0) setProductId(String(active[0].id));
    });
  }, []);

  const load = useCallback(() => {
    if (!productId) return;
    setLoading(true);
    pdirAPI
      .listParameters(productId)
      .then((r) => setParams(r.data || []))
      .finally(() => setLoading(false));
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm({ parameter: "", instrument: "" });
    setEditingId(null);
  };

  const submit = async () => {
    if (!form.parameter.trim() || !form.instrument.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await pdirAPI.updateParameter(editingId, form);
      } else {
        await pdirAPI.createParameter({
          ...form,
          product_id: productId,
          sort_order: params.length,
        });
      }
      resetForm();
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ parameter: p.parameter, instrument: p.instrument });
  };

  const toggle = async (id) => {
    await pdirAPI.toggleParameter(id);
    load();
  };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>PDIR Master</h1>
        <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
          Define the inspection parameter checklist for each part
        </p>
      </div>

      <div className="card" style={{ marginBottom: 20, maxWidth: 320 }}>
        <div className="form-group">
          <label>Part</label>
          <select
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
              resetForm();
            }}
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Add / Edit parameter form */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
          {editingId ? "Edit Parameter" : "Add New Parameter"}
        </h3>
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div className="form-group" style={{ flex: "1 1 320px" }}>
            <label>Parameter</label>
            <input
              type="text"
              placeholder="e.g. CD- 135.5 ±0.5 (SC)"
              value={form.parameter}
              onChange={(e) => setForm({ ...form, parameter: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ flex: "1 1 200px" }}>
            <label>Instrument</label>
            <input
              type="text"
              placeholder="e.g. Digital Vernier"
              value={form.instrument}
              onChange={(e) => setForm({ ...form, instrument: e.target.value })}
            />
          </div>
          <button className="btn-primary" onClick={submit} disabled={saving}>
            {editingId ? "Update" : "Add"}
          </button>
          {editingId && (
            <button className="btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Parameter list */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div
            style={{ padding: 24, textAlign: "center", color: "var(--text2)" }}
          >
            Loading...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 60 }}>S.N.</th>
                <th>Parameter</th>
                <th style={{ width: 180 }}>Instrument</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {params.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "var(--text3)",
                    }}
                  >
                    No parameters yet — add one above
                  </td>
                </tr>
              )}
              {params.map((p, i) => (
                <tr key={p.id}>
                  <td className="text3">{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{p.parameter}</td>
                  <td className="text2">{p.instrument}</td>
                  <td>
                    <span
                      className={`badge ${p.status === "Active" ? "badge-success" : "badge-danger"}`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        title="Edit"
                        onClick={() => startEdit(p)}
                        style={iconBtn}
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        title="Toggle status"
                        onClick={() => toggle(p.id)}
                        style={iconBtn}
                      >
                        <Power size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  borderRadius: 6,
  padding: 5,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};
